// The activity check on a page: the badge, the facts behind it, and the banner that
// follows a bulk check. The quote is a stranger's words, so it's rendered as text.

import { Link } from 'react-router-dom'
import type { ActivityFacts } from '@/api/types'
import { Badge, Button, InlineAlert, KeyValueList } from '@/components/ui'
import { formatCount, formatDateTime } from '@/lib/format'
import { ACTIVITY_LABELS, ACTIVITY_TONE, activityDay, NOT_CHECKED } from '@/lib/growth'
import { useActivityRun } from '@/lib/useFinder'
import { SafeExternalLink } from './shared'

export function ActivityBadge({ facts }: { facts: Pick<ActivityFacts, 'activity_status' | 'activity_checked_at'> }) {
  if (!facts.activity_checked_at) return <Badge tone="outline">{NOT_CHECKED}</Badge>
  return <Badge tone={ACTIVITY_TONE[facts.activity_status]}>{ACTIVITY_LABELS[facts.activity_status]}</Badge>
}

/** The badge and the date under it, for a table cell. */
export function ActivityCell({ facts }: { facts: ActivityFacts }) {
  const day = activityDay(facts.last_activity_on)
  return (
    <span className="grid justify-items-start gap-0.5">
      <ActivityBadge facts={facts} />
      {day ? <span className="text-xs text-muted">{day}</span> : null}
    </span>
  )
}

/** Status, date, the evidence and where it came from. */
export function ActivityDetails({ facts }: { facts: ActivityFacts }) {
  if (!facts.activity_checked_at) return <p className="text-[13px] text-muted">Not checked yet.</p>
  return (
    <div className="grid gap-3">
      <KeyValueList
        items={[
          { label: 'Status', value: <ActivityBadge facts={facts} /> },
          { label: 'Last activity', value: activityDay(facts.last_activity_on) ?? 'None found' },
          { label: 'Checked', value: formatDateTime(facts.activity_checked_at) },
        ]}
      />
      {facts.activity_evidence ? <blockquote className="break-words border-l-2 border-line-strong pl-3 text-[13px] text-ink">{facts.activity_evidence}</blockquote> : null}
      {facts.activity_evidence_url ? <SafeExternalLink href={facts.activity_evidence_url} /> : null}
      {!facts.activity_evidence ? <p className="text-[13px] text-muted">Nothing on its pages shows a dated game, season or registration. Footers, copyright years and old records don't count.</p> : null}
    </div>
  )
}

/** A run over leads going or just finished. Nothing at all when there's neither. */
export function ActivityRunBanner({ follow, words = { done: 'Activity check finished', doing: 'Checking activity', name: 'Activity check' } }: { follow: ReturnType<typeof useActivityRun>; words?: { done: string; doing: string; name: string } }) {
  const { run, stop } = follow
  if (!run) return null
  const running = run.status === 'running'
  if (!running) {
    return (
      <InlineAlert tone={run.status === 'failed' ? 'bad' : run.status === 'stopped' ? 'warn' : 'info'} title={words.done}>
        {run.note ?? `${formatCount(run.items_done)} checked.`}{' '}
        <Link to={`/growth/finder/runs/${run.id}`} className="font-semibold underline">
          See the log
        </Link>
      </InlineAlert>
    )
  }
  const percent = run.items_total > 0 ? Math.round((run.items_done / run.items_total) * 100) : 0
  return (
    <div role="status" className="grid gap-2 rounded-lg border border-line-strong bg-raised px-3 py-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-[13px] font-semibold text-ink">
          {words.doing}: {formatCount(run.items_done)} of {formatCount(run.items_total)}
        </span>
        <span className="flex items-center gap-2">
          <Link to={`/growth/finder/runs/${run.id}`} className="text-xs font-semibold text-muted underline hover:text-accent">
            See the log
          </Link>
          <Button size="sm" disabled={run.stop_requested} onClick={() => void stop()}>
            {run.stop_requested ? 'Stopping…' : 'Stop'}
          </Button>
        </span>
      </div>
      <div role="progressbar" aria-label={words.name} aria-valuemin={0} aria-valuemax={run.items_total} aria-valuenow={run.items_done} className="h-1.5 overflow-hidden rounded-full bg-hover">
        <div className="h-full rounded-full bg-accent-solid" style={{ width: `${percent}%` }} />
      </div>
    </div>
  )
}
